import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Pencil, Trash2, Briefcase } from 'lucide-react';
import { WorkExperienceForm } from './work-experience-form';
import { useWorkExperience } from '@/lib/hooks/use-work-experience';
import { IncompleteSectionWrapper } from './incomplete-section-wrapper';
import { SectionHeader } from './section-header';
import { EmptySectionPlaceholder } from './empty-section-placeholder';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Tables, TablesInsert } from '@/lib/supabase/supabase-types';

type WorkExperience = Tables<'work_experience'>;

interface WorkExperienceSectionProps {
  freelancerProfileId?: string;
  isOwner?: boolean;
  className?: string;
}

function formatDate(dateString: string): string {
  // Parse as UTC to avoid timezone offset shifting the date backward
  // e.g. "2010-01-01" parsed as local time in UTC-5 becomes Dec 31 2009
  const parts = dateString.split('-');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
  const date = new Date(year, month, parseInt(parts[2], 10) || 1);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
}

function formatDateRange(startDate: string, endDate: string | null): string {
  const start = formatDate(startDate);
  const end = endDate ? formatDate(endDate) : 'Present';
  return `${start} - ${end}`;
}

function WorkExperienceEntry({
  experience,
  isOwner,
  onEdit,
  onDelete,
  isLast = false,
  totalCount = 1,
}: {
  experience: WorkExperience;
  isOwner: boolean;
  onEdit: (exp: WorkExperience) => void;
  onDelete: (exp: WorkExperience) => void;
  isLast?: boolean;
  totalCount?: number;
}) {
  const dateRange = formatDateRange(experience.start_date, experience.end_date);
  const descriptionLines = experience.description
    ? experience.description.split('\n').filter(line => line.trim())
    : [];

  return (
    <div className="relative flex gap-6 pb-8 last:pb-0">
      {/* Timeline Line */}
      <div className="relative flex flex-col items-center flex-shrink-0 w-6 min-h-full">
        {/* Continuous vertical line through all dots */}
        {totalCount === 1 ? (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-0.5 h-[85%] bg-slate-200" />
        ) : !isLast ? (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-slate-200" />
        ) : (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-0.5 h-[85%] bg-slate-200" />
        )}
        {/* Circle Marker with thick blue outer border */}
        <div className="relative z-10 w-6 h-6 flex items-center justify-center">
          {/* Inner circle */}
          <div className="w-4 h-4 rounded-full bg-[#4669ed] border-2 border-white shadow-sm relative z-10" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 pt-0.5 pb-6">
        <div className="bg-white rounded-[20px] border border-slate-200 p-6 shadow-sm">
          {/* Header with Edit/Delete buttons */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-slate-900 mb-1">
                {experience.job_title} - {experience.company}
              </h4>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span>{dateRange}</span>
                {experience.location && (
                  <>
                    <span>•</span>
                    <span>{experience.location}</span>
                  </>
                )}
              </div>
            </div>
            {isOwner && (
              <div className="flex gap-2 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(experience)}
                  className="h-8 w-8 p-0 text-slate-400 hover:text-[#4669ed]"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(experience)}
                  className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Description */}
          {descriptionLines.length > 0 && (
            <div className="mt-4 space-y-2">
              {descriptionLines.map((line, index) => (
                <p key={index} className="text-sm text-slate-700 leading-relaxed">
                  {line.trim().startsWith('•') || line.trim().startsWith('-') 
                    ? line.trim() 
                    : `• ${line.trim()}`}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function WorkExperienceSection({
  freelancerProfileId,
  isOwner = false,
  className,
}: WorkExperienceSectionProps) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingExperience, setEditingExperience] = useState<WorkExperience | null>(null);
  const [deletingExperience, setDeletingExperience] = useState<WorkExperience | null>(null);

  const {
    experiences,
    isLoading,
    createExperience,
    updateExperience,
    deleteExperience,
    isCreating,
    isUpdating,
    isDeleting,
  } = useWorkExperience(freelancerProfileId);

  const handleCreateExperience = async (
    data: Omit<TablesInsert<'work_experience'>, 'freelancer_profile_id'>
  ) => {
    try {
      await createExperience(data);
      setIsFormOpen(false);
    } catch {
      // Error handling is done in the hook
    }
  };

  const handleUpdateExperience = async (data: { id: string } & Partial<WorkExperience>) => {
    try {
      await updateExperience(data);
      setEditingExperience(null);
      setIsFormOpen(false);
    } catch {
      // Error handling is done in the hook
    }
  };

  const handleDeleteExperience = async () => {
    if (!deletingExperience) return;

    try {
      await deleteExperience(deletingExperience.id);
      setDeletingExperience(null);
    } catch {
      // Error handling is done in the hook
    }
  };

  const handleEditExperience = (experience: WorkExperience) => {
    setEditingExperience(experience);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingExperience(null);
  };

  const hasExperiences = experiences.length > 0;

  return (
    <IncompleteSectionWrapper isComplete={hasExperiences || !isOwner} className={className}>
      <SectionHeader
        icon={Briefcase}
        title="Work Experience"
        isComplete={hasExperiences || !isOwner}
      />

      {isLoading && (
        <div className="text-center py-8">
          <p className="text-slate-400">Loading work experience...</p>
        </div>
      )}

      {!isLoading && !hasExperiences && isOwner && (
        <EmptySectionPlaceholder
          icon={Briefcase}
          title="Add Your Work Experience"
          description="Showcase your professional experience and demonstrate your expertise to potential clients."
          buttonLabel="Add Experience"
          onButtonClick={() => setIsFormOpen(true)}
        />
      )}

      {!isLoading && !hasExperiences && !isOwner && (
        <div className="text-center py-8">
          <p className="text-slate-400">No work experience to display.</p>
        </div>
      )}

      {hasExperiences && (
        <>
          {isOwner && (
            <div className="mb-6">
              <Button
                onClick={() => setIsFormOpen(true)}
                variant="outline"
                className="rounded-[48px] border-2 border-slate-200"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Experience
              </Button>
            </div>
          )}

          {/* Timeline Container */}
          <div className="relative">
            {experiences.map((experience, index) => (
              <WorkExperienceEntry
                key={experience.id}
                experience={experience}
                isOwner={isOwner}
                onEdit={handleEditExperience}
                onDelete={(exp) => setDeletingExperience(exp)}
                isLast={index === experiences.length - 1}
                totalCount={experiences.length}
              />
            ))}
          </div>
        </>
      )}

      {/* Experience Form Modal */}
      <WorkExperienceForm
        experience={editingExperience || undefined}
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleCreateExperience}
        onUpdate={handleUpdateExperience}
        isLoading={isCreating || isUpdating}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingExperience} onOpenChange={() => setDeletingExperience(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Work Experience</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingExperience?.job_title} at {deletingExperience?.company}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteExperience}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </IncompleteSectionWrapper>
  );
}

